import _ from 'lodash';

import { NodeIds } from '@breathelife/insurance-form-builder';
import { QuestionnaireVersionPricingNodeIds } from '@breathelife/types';
import { InstanceScope, QuestionnaireEngine, VersionedAnswers } from '@zinnia/form-engine-sdk';
import { AdoMinMaxConfig, EquivalentSingleAgeConfig } from '../../Models/PlatformFeatures';

type HasIncludesParams = {
  answers: VersionedAnswers;
  featureConfigsAdoMinMax: AdoMinMaxConfig;
  featureConfigsESA: EquivalentSingleAgeConfig;
  nodeIdsAffectingPricing: string[];
  updatedNodeIds: string[];
  questionnaireEngine: QuestionnaireEngine;
  pricingFieldIdentifiers?: QuestionnaireVersionPricingNodeIds;
};

// Checks whether the conditions are met to fetch extra includes values for pricing
export function hasIncludes(props: HasIncludesParams): { ado: boolean; esa: boolean } {
  const ado = shouldIncludeAdoMinMax(props);
  const esa = shouldFetchESA(props);

  return {
    ado,
    esa,
  };
}

function shouldIncludeAdoMinMax({
  updatedNodeIds,
  featureConfigsAdoMinMax,
  questionnaireEngine,
  nodeIdsAffectingPricing,
  pricingFieldIdentifiers,
}: HasIncludesParams): boolean {
  if (!featureConfigsAdoMinMax.enabled) return false;

  const fieldIdentifiersToWatch = pricingFieldIdentifiers ? pricingFieldIdentifiers.nodeIds : nodeIdsAffectingPricing;
  const updatedIdentifiersFields = _.isArray(fieldIdentifiersToWatch)
    ? _.intersection(fieldIdentifiersToWatch, updatedNodeIds)
    : [];
  const isUpdatedFieldOnlyAdoAmount =
    updatedIdentifiersFields.length === 1 &&
    featureConfigsAdoMinMax.adoCoverageAmount &&
    updatedIdentifiersFields.includes(featureConfigsAdoMinMax.adoCoverageAmount);
  const shouldRefreshAdo = updatedIdentifiersFields.length > 0 && !isUpdatedFieldOnlyAdoAmount;

  const adoOptionNodeId = featureConfigsAdoMinMax.adoOptionNodeId;
  if (!adoOptionNodeId || !shouldRefreshAdo) return false;

  const proposedInsuredCount = questionnaireEngine.getRepetitionCount(NodeIds.insuredPeople, {});
  if (!proposedInsuredCount) return false;

  let shouldRefetch: boolean = false;
  for (let proposedInsuredIndex = 0; proposedInsuredIndex < proposedInsuredCount; proposedInsuredIndex++) {
    const scope: InstanceScope = {
      [NodeIds.insuredPeople]: proposedInsuredIndex,
    };

    const adoOption = questionnaireEngine.getAnswer(adoOptionNodeId, scope);
    const adoIsNotEnabled = !adoOption || adoOption === 'no';
    if (!adoIsNotEnabled) {
      shouldRefetch = true;
      break;
    }
  }
  return shouldRefetch;
}

// Checks whether the conditions are met to fetch Equivalent Single Age (ESA)
function shouldFetchESA({
  updatedNodeIds,
  featureConfigsESA,
  questionnaireEngine,
  nodeIdsAffectingPricing,
  pricingFieldIdentifiers,
}: HasIncludesParams): boolean {
  if (
    !featureConfigsESA.enabled ||
    !featureConfigsESA.selectedProductNodeId ||
    !featureConfigsESA.jointProducts ||
    featureConfigsESA.jointProducts.length === 0 ||
    !featureConfigsESA.insuredInfoNodeIds ||
    featureConfigsESA.insuredInfoNodeIds.length === 0
  )
    return false;

  const fieldIdentifiersToWatch = pricingFieldIdentifiers ? pricingFieldIdentifiers.nodeIds : nodeIdsAffectingPricing;
  const updatedIdentifiersFields = _.isArray(fieldIdentifiersToWatch)
    ? _.intersection(fieldIdentifiersToWatch, updatedNodeIds)
    : [];

  if (updatedIdentifiersFields.length === 0) return false;

  const proposedInsuredCount = questionnaireEngine.getRepetitionCount(NodeIds.insuredPeople, {});
  if (!proposedInsuredCount || proposedInsuredCount !== 2) return false;

  let isJointProduct = false;
  let isAllInsuredInfoAvailable = true;

  for (let proposedInsuredIndex = 0; proposedInsuredIndex < proposedInsuredCount; proposedInsuredIndex++) {
    const scope: InstanceScope = {
      [NodeIds.insuredPeople]: proposedInsuredIndex,
    };

    const selectedProduct = questionnaireEngine.getAnswer(featureConfigsESA.selectedProductNodeId, scope);
    if (selectedProduct && featureConfigsESA.jointProducts.includes(selectedProduct as string)) {
      isJointProduct = true;
    }

    for (const nodeId of featureConfigsESA.insuredInfoNodeIds) {
      if (!questionnaireEngine.getAnswer(nodeId, scope)) {
        isAllInsuredInfoAvailable = false;
      }
    }
  }

  return isJointProduct && isAllInsuredInfoAvailable;
}
